import styled, { StyledComponentPropsWithRef } from 'styled-components'


import { theme } from '../../styles/theme'
import { Spinner, type SpinnerProps } from '../Spinner/Spinner'
import { Label } from '../Typography/Typography'

import type { ReactNode } from 'react'

interface MaybeWithIsSaving {
  isSaving?: boolean
}

export interface FormProps
  extends StyledComponentPropsWithRef<'form'>,
    Pick<SpinnerProps, 'size' | 'spinnerTitle'>,
    MaybeWithIsSaving,
    MaybeWithIsDisabled {}

export const Form = ({ isSaving = false, ...props }: FormProps) => (
  <Spinner {...{ as: 'form', isSpinning: isSaving, size: 3, ...props }} />
)

export type FormType = typeof Form

export const FormFooter = styled.footer`
  gap: 1em;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-block-start: auto;
`

const FormFieldset = styled.fieldset`
  text-align: start;
  align-content: start;
  display: grid;
  gap: 1em;
`

const FieldsetLegend = styled(Label)`
  color: currentColor;
  inline-size: 100%;
  /* Flex column and the pseudo are necessary to compensate for the workaround of the fieldset limitation above. */
  display: flex;
  flex-direction: column;
  ${FormFieldset} > & {
    // 'gap' attribute not working on legend element, since it's not considered a grid item https://stackoverflow.com/a/65866981/4574731
    // so we just add a margin.
    margin-block-end: 1em;
  }
  ${FormFieldset} > &::after {
    content: '';
  }
  + * {
    /* This is for 'RadioButton' components, but may interfere with something else in the future. */
    padding-block-start: 0;
  }
  /* Fieldsets after the first one should have more room above them. We can't use gap. */
  ${FormFieldset}:not(:first-of-type) > & {
    padding-block-start: 1em;
  }
`

export interface FieldsetProps extends StyledComponentPropsWithRef<typeof FormFieldset> {
  label?: string | ReactNode
}

export function Fieldset({ children, label, ...props }: FieldsetProps) {
  return (
    <FormFieldset {...props}>
      {Boolean(label) ? <FieldsetLegend {...{ children: label, as: 'legend' }} /> : null}
      {children}
    </FormFieldset>
  )
}

export type FieldsetType = typeof Fieldset

const OrContent = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.tiny};
  block-size: var(--size, 3em);
  inline-size: var(--size, 3em);
  min-inline-size: var(--size, 3em);
  border-radius: 50%;
  text-transform: uppercase;
  color: ${theme.colors.White};
  font-weight: ${theme.fontWeights.bold};
  background-color: ${theme.colors.Gray7};
`

interface OrProps extends StyledComponentPropsWithRef<typeof OrContent> {}

export function Or({ children = 'or', ...props }: OrProps) {
  return <OrContent {...{ ...props, children }} />
}
